import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';

const Quiz = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    feeling: '',
    frequency: '',
  });

  const feelings = [
    { value: 'low-energy', label: 'Low Energy', icon: '⚡' },
    { value: 'sleep-imbalance', label: 'Sleep Imbalance', icon: '🌙' },
    { value: 'digestion-issues', label: 'Digestion Issues', icon: '🍽️' },
    { value: 'stress-skin', label: 'Stress or Dull Skin', icon: '😩' },
  ];

  const frequencies = [
    { value: 'often', label: 'Often' },
    { value: 'sometimes', label: 'Sometimes' },
    { value: 'rarely', label: 'Rarely' },
  ];

  const handleSelect = (field, value) => {
    setAnswers({ ...answers, [field]: value });
    
    // Save to localStorage
    const quizProgress = { ...answers, [field]: value };
    localStorage.setItem('quizProgress', JSON.stringify(quizProgress));
  };

  const handleNext = () => {
    if (step === 1 && answers.feeling) {
      setStep(2);
    } else if (step === 2 && answers.frequency) {
      setStep(3);
      // Save completed quiz
      localStorage.setItem('quizCompleted', 'true');
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const getBenefits = () => {
    const benefitsMap = {
      'low-energy': ['Boosts metabolism & energy', 'Revives vitality', 'Improves cellular function'],
      'sleep-imbalance': ['Improves sleep quality', 'Balances hormones', 'Reduces stress'],
      'digestion-issues': ['Enhances digestion', 'Flushes toxins', 'Improves gut health'],
      'stress-skin': ['Rejuvenates skin', 'Reduces stress', 'Improves complexion'],
    };
    return benefitsMap[answers.feeling] || [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-accent to-green-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="h-2 bg-white/50 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(step / 3) * 100}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-primary"
            />
          </div>
          <p className="text-center text-gray-600 mt-2">
            Step {step} of 3
          </p>
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl p-8 shadow-xl"
        >
          {/* Step 1: How do you feel */}
          {step === 1 && (
            <>
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                How do you feel lately?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {feelings.map((feeling) => (
                  <motion.button
                    key={feeling.value}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSelect('feeling', feeling.value)}
                    className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                      answers.feeling === feeling.value
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    <div className="text-4xl mb-3">{feeling.icon}</div>
                    <p className="text-lg font-semibold text-gray-800">
                      {feeling.label}
                    </p>
                  </motion.button>
                ))}
              </div>
            </>
          )}

          {/* Step 2: Frequency */}
          {step === 2 && (
            <>
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                How often do you feel bloated or fatigued?
              </h2>
              <div className="space-y-4">
                {frequencies.map((freq) => (
                  <motion.button
                    key={freq.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSelect('frequency', freq.value)}
                    className={`w-full p-6 rounded-2xl border-2 transition-all duration-300 ${
                      answers.frequency === freq.value
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    <p className="text-xl font-semibold text-gray-800">
                      {freq.label}
                    </p>
                  </motion.button>
                ))}
              </div>
            </>
          )}

          {/* Step 3: Personalized Result */}
          {step === 3 && (
            <>
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Your personalized cleanse will help with...
              </h2>
              <div className="space-y-4 mb-8">
                {getBenefits().map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.2 }}
                    className="flex items-center gap-3 p-4 bg-primary/10 rounded-xl"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <p className="text-lg text-gray-800">{benefit}</p>
                  </motion.div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/benefits')}
                className="w-full bg-primary text-white py-4 rounded-xl text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300"
              >
                See My Detox Plan
              </motion.button>
            </>
          )}
        </motion.div>

        {/* Navigation Buttons */}
        {step < 3 && (
          <div className="flex justify-between mt-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBack}
              disabled={step === 1}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-gray-700 font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              disabled={
                (step === 1 && !answers.feeling) ||
                (step === 2 && !answers.frequency)
              }
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
